import { useEffect, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save, RotateCcw, Lock, Unlock, FlagTriangleRight } from "lucide-react";
import { Link } from "react-router-dom";

const GROUP_ORDER = ["Broad Market", "Midcap", "Smallcap", "Sectoral", "Custom"];

function Field({ label, unit, children }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted2">{label}{unit ? ` (${unit})` : ""}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function numInput(props) {
  return <Input type="number" className="bg-elevated border-edge rounded-sm mono text-sm h-10" {...props} />;
}

export default function Config() {
  const [cfg, setCfg] = useState(null);
  const [report, setReport] = useState(null);
  const [customText, setCustomText] = useState("");
  const [saving, setSaving] = useState(false);
  const [uni, setUni] = useState(null);

  const load = () => {
    api.getConfig().then((c) => {
      setCfg(c);
      setCustomText((c.custom_symbols || []).join(", "));
    });
    api.paperReport().then(setReport).catch(() => {});
    api.universe().then(setUni).catch(() => {});
  };
  useEffect(load, []);

  if (!cfg) return <div className="p-6 text-muted2 text-sm">Loading config…</div>;

  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));

  const toggleUniverse = (key) =>
    setCfg((p) => {
      const cur = p.universes || [];
      return { ...p, universes: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] };
    });

  const options = uni?.options || [];
  const groups = GROUP_ORDER
    .map((g) => ({ group: g, items: options.filter((o) => o.group === g) }))
    .filter((g) => g.items.length);
  const selectedCount = (cfg.universes || []).length;
  const selectGroup = (items, on) =>
    setCfg((p) => {
      const cur = new Set(p.universes || []);
      items.forEach((it) => (on ? cur.add(it.key) : cur.delete(it.key)));
      return { ...p, universes: [...cur] };
    });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg, custom_symbols: customText.split(/[\s,]+/).map((s) => s.trim().toUpperCase()).filter(Boolean) };
      delete payload.mode; // mode changed via toggle only
      const saved = await api.updateConfig(payload);
      setCfg(saved);
      toast.success("Config saved");
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail });
    } finally {
      setSaving(false);
    }
  };

  const toggleLive = async (toLive) => {
    try {
      const saved = await api.updateConfig({ mode: toLive ? "LIVE" : "PAPER" });
      setCfg(saved);
      toast.success(`Switched to ${saved.mode}`);
    } catch (e) {
      toast.error("Live locked", { description: e?.response?.data?.detail });
    }
  };

  const resetSession = async () => {
    await api.reset();
    toast.success("Session reset");
  };
  const finalize = async () => {
    const r = await api.finalize();
    toast.success("Session finalized", { description: `${r.trades} trades · WR ${r.win_rate}%` });
  };

  const eligible = report?.go_live_eligible;
  const approved = report?.manual_approved;
  const kiteReady = report?.kite_ready;
  const isSwing = cfg.strategy === "SWING";
  const unlocked = isSwing ? (approved && kiteReady) : report?.live_unlocked;

  return (
    <div className="pb-44">
      <div className="glass sticky top-0 z-30 border-b border-edge px-4 py-3">
        <h1 className="text-xl font-bold tracking-tight">Configuration</h1>
        <p className="text-[11px] text-muted2">Strategy parameters &amp; risk caps</p>
      </div>

      <div className="p-4 space-y-5">
        {/* Universe */}
        <div className="bg-surface border border-edge rounded-sm p-3" data-testid="universe-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted2">
              Universe <span className="text-brand">(select one or more)</span>
            </div>
            <span className="mono text-[10px] text-muted2" data-testid="universe-summary">
              {selectedCount} sel · {uni?.count ?? 0} stocks
            </span>
          </div>

          <div className="space-y-3">
            {groups.map(({ group, items }) => {
              const allOn = items.every((it) => (cfg.universes || []).includes(it.key));
              return (
                <div key={group}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-muted2/80">{group}</span>
                    {group !== "Custom" && (
                      <button
                        onClick={() => selectGroup(items, !allOn)}
                        data-testid={`universe-group-${group.replace(/\s+/g, "-").toLowerCase()}`}
                        className="text-[9px] font-semibold uppercase tracking-wider text-brand/80 active:text-brand"
                      >
                        {allOn ? "clear" : "all"}
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {items.map((u) => {
                      const active = (cfg.universes || []).includes(u.key);
                      return (
                        <button
                          key={u.key}
                          onClick={() => toggleUniverse(u.key)}
                          data-testid={`universe-${u.key}`}
                          className={`px-1 py-2 rounded-sm text-[10px] font-bold tracking-wide leading-tight transition-colors duration-150 border ${
                            active ? "bg-brand/15 border-brand text-brand" : "bg-elevated border-edge text-muted2"
                          }`}
                        >
                          <div>{u.label}</div>
                          {u.key !== "CUSTOM" && (
                            <div className="mono text-[8px] opacity-60">{u.count}</div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {(cfg.universes || []).includes("CUSTOM") && (
            <textarea
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              placeholder="RELIANCE, TCS, INFY…"
              data-testid="custom-symbols"
              className="mt-3 w-full bg-elevated border border-edge rounded-sm mono text-xs p-2 h-20 outline-none focus:border-brand"
            />
          )}
        </div>

        {/* Strategy selector */}
        <div className="bg-surface border border-edge rounded-sm p-3" data-testid="strategy-panel">
          <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted2 mb-2">Strategy</div>
          <div className="flex gap-2">
            <button
              onClick={() => set("strategy", "SCALP")}
              data-testid="strategy-scalp-btn"
              className={`flex-1 py-2.5 rounded-sm text-xs font-bold tracking-wider border ${cfg.strategy === "SCALP" ? "bg-brand/15 border-brand text-brand" : "bg-elevated border-edge text-muted2"}`}
            >
              SCALP · MIS Intraday
            </button>
            <button
              onClick={() => set("strategy", "SWING")}
              data-testid="strategy-swing-btn"
              className={`flex-1 py-2.5 rounded-sm text-xs font-bold tracking-wider border ${cfg.strategy === "SWING" ? "bg-pos/15 border-pos text-pos" : "bg-elevated border-edge text-muted2"}`}
            >
              SWING · CNC Auto
            </button>
          </div>
          {cfg.strategy === "SWING" && (
            <p className="mt-2 text-[10px] text-muted2 leading-snug">
              Fully automatic delivery bot: buys any stock crossing +{cfg.swing_entry_pct}% intraday
              (top by volume).
              {" "}{cfg.swing_stop_pct > 0
                ? `Let winners run: no fixed target — exits only when a holding pulls back ${cfg.swing_stop_pct}% from its peak.${cfg.swing_breakeven_trigger_pct > 0 ? ` Once up ${cfg.swing_breakeven_trigger_pct}%, the stop locks to breakeven (+${cfg.swing_breakeven_buffer_pct}%) so a winner can't turn into a loss.` : ""}`
                : `Rests a +${cfg.swing_target_pct}% sell target and holds across days until it fills (no trailing stop).`}
              {" "}Pick any index below (NIFTY 500, sectoral, etc.) to change the hunting universe.
            </p>
          )}
        </div>

        {cfg.strategy === "SWING" && (
          <div className="bg-surface border border-pos/30 rounded-sm p-3 grid grid-cols-2 gap-3" data-testid="swing-panel">
            <div className="col-span-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-pos">Swing Bot Parameters</div>
            <Field label="₹ / Order" unit="₹">
              {numInput({ value: cfg.swing_notional_per_order, onChange: (e) => set("swing_notional_per_order", +e.target.value), "data-testid": "swing-notional-input" })}
            </Field>
            <Field label="Capital Ceiling" unit="₹">
              {numInput({ value: cfg.swing_capital_ceiling, onChange: (e) => set("swing_capital_ceiling", +e.target.value), "data-testid": "swing-ceiling-input" })}
            </Field>
            <Field label="Entry Trigger" unit="%">
              {numInput({ step: "0.1", value: cfg.swing_entry_pct, onChange: (e) => set("swing_entry_pct", +e.target.value), "data-testid": "swing-entry-input" })}
            </Field>
            <Field label="Min Buyers (0=off)" unit="%">
              {numInput({ step: "1", value: cfg.swing_min_buyer_pct, onChange: (e) => set("swing_min_buyer_pct", +e.target.value), "data-testid": "swing-buyer-input" })}
            </Field>
            <Field label="Buyers Avg Window" unit="min">
              {numInput({ step: "1", value: cfg.swing_buyer_avg_minutes, onChange: (e) => set("swing_buyer_avg_minutes", +e.target.value), "data-testid": "swing-buyer-window-input" })}
            </Field>
            <div className="col-span-2 text-[10px] text-muted2 leading-snug">
              Min Buyers = share of the order book on the buy side, averaged over the window. Set <span className="text-white">100</span> for pure demand only (buyers, zero sellers) — fewer trades, higher conviction.
            </div>
            <Field label="Min 1-Month Growth (0=off)" unit="%">
              {numInput({ step: "0.5", value: cfg.swing_month_growth_pct, onChange: (e) => set("swing_month_growth_pct", +e.target.value), "data-testid": "swing-month-input" })}
            </Field>
            <Field label="Min Rel Volume (0=off)" unit="x">
              {numInput({ step: "0.1", value: cfg.swing_min_rel_volume, onChange: (e) => set("swing_min_rel_volume", +e.target.value), "data-testid": "swing-relvol-input" })}
            </Field>
            <div className="col-span-2 flex items-center justify-between bg-elevated border border-edge rounded-sm px-3 py-2">
              <span className="text-xs text-white">Require Price &gt; VWAP <span className="text-muted2">(only buy above the day VWAP)</span></span>
              <Switch checked={!!cfg.swing_require_vwap} onCheckedChange={(v) => set("swing_require_vwap", v)} data-testid="swing-vwap-toggle" />
            </div>
            <div className="col-span-2 text-[10px] text-muted2 leading-snug">
              Price &gt; VWAP keeps buys on the demand side of the day's average. Min Rel Volume = volume pace vs the 20-day average (e.g. <span className="text-white">1.0</span> = trading at normal pace, <span className="text-white">1.5</span> = 50% heavier than usual). Both OFF by default.
            </div>
            <Field label="Target" unit="%">
              {numInput({ step: "0.1", value: cfg.swing_target_pct, onChange: (e) => set("swing_target_pct", +e.target.value), "data-testid": "swing-target-input" })}
            </Field>
            <Field label="Trailing Stop (0 = off)" unit="%">
              {numInput({ step: "0.1", value: cfg.swing_stop_pct, onChange: (e) => set("swing_stop_pct", +e.target.value), "data-testid": "swing-stop-input" })}
            </Field>
            <Field label="Max Positions">
              {numInput({ value: cfg.swing_max_positions, onChange: (e) => set("swing_max_positions", +e.target.value), "data-testid": "swing-maxpos-input" })}
            </Field>
            <Field label="Max Buys / Symbol / Day">
              {numInput({ value: cfg.swing_max_buys_per_symbol, onChange: (e) => set("swing_max_buys_per_symbol", +e.target.value), "data-testid": "swing-persymbol-input" })}
            </Field>
            <Field label="Breakeven Lock @ (0 = off)" unit="%">
              {numInput({ step: "0.1", value: cfg.swing_breakeven_trigger_pct, onChange: (e) => set("swing_breakeven_trigger_pct", +e.target.value), "data-testid": "swing-belock-input" })}
            </Field>
            <Field label="B/E Floor Buffer" unit="%">
              {numInput({ step: "0.1", value: cfg.swing_breakeven_buffer_pct, onChange: (e) => set("swing_breakeven_buffer_pct", +e.target.value), "data-testid": "swing-bebuffer-input" })}
            </Field>
            <Field label="Stop Grace After Open (0 = off)" unit="min">
              {numInput({ value: cfg.swing_stop_grace_minutes, onChange: (e) => set("swing_stop_grace_minutes", +e.target.value), "data-testid": "swing-stop-grace-input" })}
            </Field>
            <Field label="No New Buys After" unit="IST">
              {numInput({ type: "time", value: cfg.swing_buy_cutoff || "", onChange: (e) => set("swing_buy_cutoff", e.target.value), "data-testid": "swing-buy-cutoff-input" })}
            </Field>
            <Field label="Release to Demat After" unit="sessions">
              {numInput({ value: cfg.swing_release_sessions, onChange: (e) => set("swing_release_sessions", +e.target.value), "data-testid": "swing-release-sessions-input" })}
            </Field>
            <div className="flex items-end text-[10px] text-muted2 pb-1 leading-snug">Holdings older than this many trading sessions move to your "Released to Demat" list (kept, not sold; slot freed)</div>
            <div className="flex items-end text-[10px] text-muted2 pb-1">Sell one → a slot frees for a new buy</div>
            <div className="col-span-2 flex items-center justify-between bg-elevated border border-edge rounded-sm px-3 py-2">
              <span className="text-xs text-white">Opening Analyzer <span className="text-muted2">(observe first N min, no new buys)</span></span>
              <Switch checked={!!cfg.swing_opening_analyzer} onCheckedChange={(v) => set("swing_opening_analyzer", v)} data-testid="swing-analyzer-toggle" />
            </div>
            {cfg.swing_opening_analyzer && (
              <>
                <Field label="Observe Window" unit="min">
                  {numInput({ value: cfg.swing_analyzer_minutes, onChange: (e) => set("swing_analyzer_minutes", +e.target.value), "data-testid": "swing-analyzer-minutes-input" })}
                </Field>
                <div className="flex items-end text-[10px] text-muted2 pb-1 leading-snug">
                  Bot analyzes the universe for {cfg.swing_analyzer_minutes || 15} min after 09:15, then buys the strongest uptrend (gain since open) × volume mover. Existing holdings stay managed.
                </div>
              </>
            )}
            <div className="col-span-2 flex items-center justify-between bg-elevated border border-edge rounded-sm px-3 py-2">
              <span className="text-xs text-white">Circuit-Band Filter <span className="text-muted2">(only buy 10–20% band stocks)</span></span>
              <Switch checked={!!cfg.swing_circuit_filter} onCheckedChange={(v) => set("swing_circuit_filter", v)} data-testid="swing-circuit-toggle" />
            </div>
            {cfg.swing_circuit_filter && (
              <>
                <Field label="Circuit Band Min" unit="%">
                  {numInput({ step: "1", value: cfg.swing_circuit_min_pct, onChange: (e) => set("swing_circuit_min_pct", +e.target.value), "data-testid": "swing-circuit-min-input" })}
                </Field>
                <Field label="Circuit Band Max" unit="%">
                  {numInput({ step: "1", value: cfg.swing_circuit_max_pct, onChange: (e) => set("swing_circuit_max_pct", +e.target.value), "data-testid": "swing-circuit-max-input" })}
                </Field>
                <div className="col-span-2 text-[10px] text-muted2 leading-snug">
                  Only scans stocks whose NSE daily price band is between {cfg.swing_circuit_min_pct}% and {cfg.swing_circuit_max_pct}% (the wider, more tradable bands). Tighter 2%/5% band names are skipped.
                </div>
              </>
            )}
          </div>
        )}

        {/* Sizing & signal — scalp only */}
        {cfg.strategy === "SCALP" && (
        <div className="bg-surface border border-edge rounded-sm p-3 grid grid-cols-2 gap-3">
          <div className="col-span-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-muted2">Scalp (MIS) Parameters</div>
          <Field label="Notional / Stock" unit="₹">
            {numInput({ value: cfg.notional_per_stock, onChange: (e) => set("notional_per_stock", +e.target.value), "data-testid": "notional-input" })}
          </Field>
          <Field label="Momentum Trigger" unit="%">
            {numInput({ step: "0.1", value: cfg.momentum_threshold, onChange: (e) => set("momentum_threshold", +e.target.value), "data-testid": "momentum-input" })}
          </Field>
          <Field label="Target" unit="%">
            {numInput({ step: "0.1", value: cfg.target_pct, onChange: (e) => set("target_pct", +e.target.value), "data-testid": "target-input" })}
          </Field>
          <Field label="Stop Loss" unit="%">
            {numInput({ step: "0.1", value: cfg.stop_pct, onChange: (e) => set("stop_pct", +e.target.value), "data-testid": "stop-input" })}
          </Field>
        </div>
        )}

        {/* Caps — scalp only */}
        {cfg.strategy === "SCALP" && (
        <div className="bg-surface border border-edge rounded-sm p-3 grid grid-cols-2 gap-3">
          <div className="col-span-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-muted2">Risk Caps</div>
          <Field label="Max Concurrent">
            {numInput({ value: cfg.max_concurrent, onChange: (e) => set("max_concurrent", +e.target.value), "data-testid": "concurrent-input" })}
          </Field>
          <Field label="Max Trades / Day">
            {numInput({ value: cfg.max_trades_day, onChange: (e) => set("max_trades_day", +e.target.value), "data-testid": "maxtrades-input" })}
          </Field>
          <Field label="Max / Symbol / Day">
            {numInput({ value: cfg.max_per_symbol_day, onChange: (e) => set("max_per_symbol_day", +e.target.value), "data-testid": "persymbol-input" })}
          </Field>
          <Field label="Daily Loss Kill" unit="%">
            {numInput({ step: "0.1", value: cfg.daily_loss_kill_pct, onChange: (e) => set("daily_loss_kill_pct", +e.target.value), "data-testid": "losskill-input" })}
          </Field>
          <div className="col-span-2 flex items-center justify-between bg-elevated border border-edge rounded-sm px-3 py-2">
            <span className="text-xs text-white">Simulation always-on <span className="text-muted2">(ignore market clock)</span></span>
            <Switch checked={cfg.simulation_always_on} onCheckedChange={(v) => set("simulation_always_on", v)} data-testid="sim-toggle" />
          </div>
        </div>
        )}

        <button onClick={save} disabled={saving} className="w-full flex items-center justify-center gap-2 py-3 rounded-sm bg-pos text-black font-bold tracking-wider active:scale-[0.99] transition-transform duration-150" data-testid="save-config-btn">
          <Save size={16} strokeWidth={2.5} /> {saving ? "SAVING…" : "SAVE CONFIG"}
        </button>

        {/* Double-gated Live toggle */}
        <div className="bg-surface border border-edge rounded-sm p-4" data-testid="live-gate-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {unlocked ? <Unlock size={16} className="text-pos" /> : <Lock size={16} className="text-neg" />}
              <span className="text-sm font-bold tracking-wide">Live Trading Gate</span>
            </div>
            <span className={`mono text-xs font-bold ${cfg.mode === "LIVE" ? "text-neg" : "text-brand"}`}>{cfg.mode}</span>
          </div>

          <GateRow ok={eligible} label={`Paper win-rate ≥ ${report?.gate ?? 80}%`} detail={isSwing ? "N/A for swing" : `${report?.win_rate ?? 0}% over ${report?.total_trades ?? 0} trades`} skip={isSwing} />
          <GateRow ok={approved} label="Manual approval ticked" detail={approved ? "Approved" : "Pending — approve in Report"} />
          <GateRow ok={kiteReady} label="Kite keys + daily login" detail={kiteReady ? "Ready" : "Not configured"} />

          <div className="mt-3 flex gap-2">
            <button
              onClick={() => toggleLive(false)}
              className={`flex-1 py-2.5 rounded-sm text-xs font-bold tracking-wider border ${cfg.mode === "PAPER" ? "bg-brand/15 border-brand text-brand" : "bg-elevated border-edge text-muted2"}`}
              data-testid="mode-paper-btn"
            >
              PAPER
            </button>
            <button
              onClick={() => toggleLive(true)}
              disabled={!unlocked}
              className={`flex-1 py-2.5 rounded-sm text-xs font-bold tracking-wider border transition-colors duration-150 ${
                cfg.mode === "LIVE" ? "bg-neg/15 border-neg text-neg" : unlocked ? "bg-elevated border-neg/40 text-neg" : "bg-elevated border-edge text-muted2/50 cursor-not-allowed"
              }`}
              data-testid="mode-live-btn"
            >
              {unlocked ? "GO LIVE" : "LIVE 🔒"}
            </button>
          </div>
          {!unlocked && (
            <Link to="/report" className="block mt-2 text-center text-[11px] text-brand underline">Review paper test report →</Link>
          )}
        </div>

        {/* Session controls */}
        <div className="flex gap-2">
          <button onClick={resetSession} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-sm bg-elevated border border-edge text-xs font-bold tracking-wider text-white" data-testid="reset-session-btn">
            <RotateCcw size={14} /> RESET SESSION
          </button>
          <button onClick={finalize} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-sm bg-elevated border border-edge text-xs font-bold tracking-wider text-white" data-testid="finalize-btn">
            <FlagTriangleRight size={14} /> END OF DAY
          </button>
        </div>
      </div>
    </div>
  );
}

function GateRow({ ok, label, detail, skip }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-edge/50 last:border-0">
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${skip ? "bg-muted2/40" : ok ? "bg-pos" : "bg-neg"}`} />
        <span className={`text-xs ${skip ? "text-muted2 line-through" : "text-white"}`}>{label}</span>
      </div>
      <span className={`mono text-[10px] ${ok && !skip ? "text-pos" : "text-muted2"}`}>{detail}</span>
    </div>
  );
}
